import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { login, requestPasswordReset } from '../api';
import './AuthPage.css';

export const LoginPage = () => {
  const [language, setLanguage] = useState(() => localStorage.getItem('fundLanguage') || 'cs');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [twoFactorToken, setTwoFactorToken] = useState('');
  const [requiresTwoFactor, setRequiresTwoFactor] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  const text = {
    cs: {
      title: 'Vojtovo partnerství',
      subtitle: 'Přihlášení do partnerského portálu',
      email: 'Email',
      password: 'Heslo',
      showPassword: 'Zobrazit',
      hidePassword: 'Skrýt',
      twoFactor: '2FA kód',
      loading: 'Přihlašuji...',
      login: 'Přihlásit',
      forgot: 'Zapomenuté heslo',
      loginFailed: 'Přihlášení selhalo',
      resetFailed: 'Nepodařilo se vytvořit reset hesla',
      resetSent: 'Pokud email existuje, admin dostane žádost o reset hesla.',
      switchLanguage: 'English'
    },
    en: {
      title: 'Vojtovo Partnership',
      subtitle: 'Partner portal login',
      email: 'Email',
      password: 'Password',
      showPassword: 'Show',
      hidePassword: 'Hide',
      twoFactor: '2FA code',
      loading: 'Loading...',
      login: 'Login',
      forgot: 'Forgot password',
      loginFailed: 'Login failed',
      resetFailed: 'Could not create password reset request',
      resetSent: 'If the email exists, the admin will receive a password reset request.',
      switchLanguage: 'Čeština'
    }
  }[language] || {};

  const toggleLanguage = () => {
    const nextLanguage = language === 'cs' ? 'en' : 'cs';
    setLanguage(nextLanguage);
    localStorage.setItem('fundLanguage', nextLanguage);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const { data } = await login(email, password, twoFactorToken);
      authLogin(data.user, data.token);
      navigate('/dashboard');
    } catch (err) {
      if (err.response?.data?.requiresTwoFactor) {
        setRequiresTwoFactor(true);
      }
      setError(err.response?.data?.error || text.loginFailed);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestReset = async () => {
    setError('');
    setMessage('');

    try {
      await requestPasswordReset(email);
      setMessage(text.resetSent);
    } catch (err) {
      setError(err.response?.data?.error || text.resetFailed);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <button type="button" className="auth-language-btn" onClick={toggleLanguage}>
          {language === 'cs' ? 'CZ' : 'EN'} · {text.switchLanguage}
        </button>
        <h1>{text.title}</h1>
        <p className="auth-subtitle">{text.subtitle}</p>
        <form onSubmit={handleSubmit}>
          <input
            type="email"
            placeholder={text.email}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <div className="password-field">
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder={text.password}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword((visible) => !visible)}
            >
              {showPassword ? text.hidePassword : text.showPassword}
            </button>
          </div>
          {requiresTwoFactor && (
            <input
              type="text"
              inputMode="numeric"
              placeholder={text.twoFactor}
              value={twoFactorToken}
              onChange={(e) => setTwoFactorToken(e.target.value)}
              required
            />
          )}
          {error && <div className="error">{error}</div>}
          {message && <div className="success">{message}</div>}
          <button type="submit" disabled={loading}>
            {loading ? text.loading : text.login}
          </button>
        </form>
        <button type="button" className="link-button" onClick={handleRequestReset}>
          {text.forgot}
        </button>
      </div>
    </div>
  );
};
